// ============================================================================
// CompensationSection (L16 §B.5, F-845..F-849) — remuneration, LECTURE SEULE
// ============================================================================
// SENSIBLE : rendu uniquement si le SERVEUR a accorde l'onglet (soi-meme OU
// canReadCompensation) — il ne renvoie meme pas `compensation` sinon.
//
// GARDE-FOU M-816 : ce composant ne peut PAS rendre off_grid_reason_private ni
// role_fit_note — le serveur ne les SELECT pas (repositories/compensation.ts). La
// seule raison qui sort cote employe est `offGridReasonPublic` (F-848).
//
// FRAG 7 (PDR-PRO-02) : StatCards F-847 completes (salaire/boni/avantages/total)
// + frise SalaryProgression F-849. Aucun montant logge cote client.

import { Wallet } from 'lucide-react';
import { EmptyState } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { maxEchelonForNiveau, resolveNiveau } from '../../lib/salary/niveau';
import type { ProfileCompensation } from '../../hooks/useProfile';
import { SalaryProgression } from '../employee/SalaryProgression';
import type { SalaryTimeline } from '../../lib/salary/progression';

const CARD = 'rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800';

function StatTile({ label, value, hint, tone }: {
  label: string; value: string; hint: string; tone?: 'brand' | 'success';
}) {
  const valueTone =
    tone === 'brand' ? 'text-brand-600 dark:text-brand-400'
    : tone === 'success' ? 'text-emerald-700 dark:text-emerald-400'
    : 'text-slate-800 dark:text-slate-100';
  return (
    <div className={CARD}>
      <p className="text-chip font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-1 text-head font-bold tabular-nums ${valueTone}`}>{value}</p>
      <p className="mt-0.5 text-meta text-slate-500 dark:text-slate-400">{hint}</p>
    </div>
  );
}

export interface CompensationSectionProps {
  compensation: ProfileCompensation | null;
}

export function CompensationSection({ compensation }: CompensationSectionProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('en') ? 'en-CA' : 'fr-CA';
  const currentYear = new Date().getFullYear();

  const money = (n: number) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(n);
  const hourly = (n: number) =>
    t('gest:profile.compensation.hourly', {
      rate: new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n),
    });
  const pctPlain = (fraction: number) =>
    new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(fraction);

  // F-845 : aucune fiche, ou fiche non positionnee sur une classe.
  if (!compensation || !compensation.classId) {
    return (
      <section className="flex flex-col gap-3">
        <EmptyState
          icon={<Wallet size={48} aria-hidden="true" />}
          title={t('gest:profile.compensation.emptyTitle')}
          description={t('gest:profile.compensation.emptyDescription')}
        />
      </section>
    );
  }

  // F-846 : la classe n'existe plus dans la grille de l'annee courante (M-812).
  const cls = compensation.gridClass;
  if (!cls) {
    return (
      <section className="flex flex-col gap-3">
        <EmptyState
          icon={<Wallet size={48} aria-hidden="true" />}
          title={t('gest:profile.compensation.classGoneTitle')}
          description={t('gest:profile.compensation.classGoneDescription')}
        />
      </section>
    );
  }

  // M-814 : niveau REEL persiste (jamais deduit de l'echelon — les plages se
  // chevauchent) ; le plafond du niveau borne l'avancement. Les libs niveau.ts
  // attendent la forme PoC (`echelon_max`) — adaptation locale du DTO camelCase.
  const niveaux = cls.niveaux.map((n) => ({
    level: n.level,
    label: n.label,
    echelon_max: n.echelonMax,
    description: null,
  }));
  const clsForNiveau = {
    echelons: cls.echelons.map((e) => ({
      echelon: e.echelon, rate: e.rate, rate_2025: null, pct_from_prev: null,
    })),
    niveaux,
  };
  const niveau = resolveNiveau(niveaux, compensation.currentNiveau);
  const cap = maxEchelonForNiveau(clsForNiveau, compensation.currentNiveau);
  const next = compensation.currentEchelon + 1 <= cap
    ? cls.echelons.find((e) => e.echelon === compensation.currentEchelon + 1)
    : undefined;
  const atCap = niveau != null && compensation.currentEchelon >= cap;

  const gridRate = cls.echelons.find((e) => e.echelon === compensation.currentEchelon)?.rate ?? null;
  const effectiveHourly = compensation.currentHourlyRate ?? gridRate;
  const offGridUnpriced = compensation.offGrid && compensation.currentHourlyRate === null;

  // FRAG 7 : montants serveur (DTO enrichi) — repli local uniquement si absents
  // (clients caches d'une ancienne build). Jamais de 0 trompeur pour « a venir ».
  const salaryAnnual = compensation.salaryAnnual;
  const bonusActual = compensation.bonusActualAmount ?? 0;
  const bonusTarget = compensation.bonusTargetAmount ?? 0;
  const benefitsAnnual = compensation.benefitsAnnual ?? 0;
  const totalAnnual = compensation.totalAnnual;

  const salaryHint = offGridUnpriced
    ? t('gest:profile.compensation.offGridUnpriced')
    : compensation.offGrid
      ? (effectiveHourly !== null ? hourly(effectiveHourly) : '—')
      : effectiveHourly !== null
        ? t('gest:profile.compensation.gridHint', {
            rate: hourly(effectiveHourly), echelon: compensation.currentEchelon, max: cls.echelons.length,
          })
        : t('gest:profile.compensation.echelonHint', {
            echelon: compensation.currentEchelon, max: cls.echelons.length,
          });

  const bonusHint = bonusTarget > 0 || bonusActual > 0
    ? t('gest:profile.compensation.bonusHint', {
        target: money(bonusTarget),
        actual: money(bonusActual),
        pct: compensation.bonusTargetPct != null ? pctPlain(compensation.bonusTargetPct) : '—',
      })
    : t('gest:profile.compensation.bonusNone');

  const bonusByYear = new Map<number, number>();
  const bonusProjByYear = new Map<number, number>();
  for (const b of compensation.bonusSeries ?? []) {
    if (b.actualAmount > 0) bonusByYear.set(b.year, b.actualAmount);
    if (b.targetAmount > 0) bonusProjByYear.set(b.year, b.targetAmount);
  }
  const avantages = benefitsAnnual > 0 ? { year: currentYear, annual: benefitsAnnual } : null;
  const timeline = compensation.timeline as SalaryTimeline | null;

  return (
    <section className="flex flex-col gap-4">
      {/* F-847 : 4 indicateurs — salaire / boni / avantages / total (FRAG 7). */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile
          tone="brand"
          label={t('gest:profile.compensation.salary', { year: currentYear })}
          value={salaryAnnual !== null && salaryAnnual !== undefined ? money(salaryAnnual) : '—'}
          hint={salaryHint}
        />
        <StatTile
          label={t('gest:profile.compensation.bonus', { year: currentYear })}
          value={money(bonusActual)}
          hint={bonusHint}
        />
        <StatTile
          label={t('gest:profile.compensation.benefits')}
          value={money(benefitsAnnual)}
          hint={t('gest:profile.compensation.benefitsHint')}
        />
        <StatTile
          tone="success"
          label={t('gest:profile.compensation.total')}
          value={totalAnnual !== null && totalAnnual !== undefined ? money(totalAnnual) : '—'}
          hint={t('gest:profile.compensation.totalHint')}
        />
      </div>

      {/* F-848 : encadre hors grille — raison PUBLIQUE seulement (M-816). */}
      {compensation.offGrid ? (
        <div role="note" className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-900 dark:bg-blue-950/40">
          <p className="text-field font-semibold text-blue-900 dark:text-blue-200">
            {t('gest:profile.compensation.offGridTitle')}
          </p>
          <p className="mt-1 text-field text-blue-800 dark:text-blue-300">
            {compensation.offGridReasonPublic ?? t('gest:profile.compensation.offGridFallback')}
          </p>
        </div>
      ) : timeline ? (
        // F-849 : frise SalaryProgression (SELECT history + rates serveur).
        <SalaryProgression
          timeline={timeline}
          bonusByYear={bonusByYear}
          bonusProjByYear={bonusProjByYear}
          avantages={avantages}
          augGrillePct={cls.augGrillePct}
        />
      ) : (
        <div className={`${CARD} flex flex-wrap items-center justify-between gap-3`}>
          <div className="min-w-0">
            <p className="text-field font-semibold text-slate-800 dark:text-slate-100">{cls.label}</p>
            <p className="mt-0.5 text-meta text-slate-500 dark:text-slate-400">
              {niveau
                ? t('gest:profile.compensation.niveauLabel', { level: niveau.level, label: niveau.label })
                : t('gest:profile.compensation.niveauUndefined')}
              {' · '}
              {t('gest:profile.compensation.gridYear', { year: compensation.gridYear })}
            </p>
          </div>
        </div>
      )}

      {!compensation.offGrid && next ? (
        <p className="text-field text-slate-500 dark:text-slate-400">
          {t('gest:profile.compensation.nextEchelon', { echelon: next.echelon, rate: hourly(next.rate) })}
        </p>
      ) : !compensation.offGrid && atCap ? (
        <p className="text-field text-slate-500 dark:text-slate-400">
          {t('gest:profile.compensation.atCap', { cap, total: cls.echelons.length })}
        </p>
      ) : null}
    </section>
  );
}
